/* Whitehouse Lighting & Electrical — Instagram Product Card Exporter v3.5
 * Standalone browser renderer, no dependencies, no external tracking.
 * Card resolution: 1080 x 1350. Public test interface: window.WHLECardExporter.
 */
(() => {
  'use strict';
  if (window.WHLECardExporter && window.WHLECardExporter.version === '3.5.0') return;

  const W = 1080, H = 1350;
  const C = Object.freeze({
    bg: '#050a10', panel: '#07141f', panel2: '#0b2030', cyan: '#25c5ff', blue: '#0877ff',
    white: '#fafdff', muted: '#adc4d3', divider: '#5a7a90', text: '#ffffff'
  });
  const FONT = '"Poppins", "Montserrat", Arial, Helvetica, sans-serif';
  const BOX = Object.freeze({
    logo: [245, 24, 590, 95], house: [892, 16, 148, 146],
    title: [76, 154, 928, 104], summary: [108, 262, 864, 58],
    left: [44, 322, 388, 650], hero: [457, 322, 580, 392],
    thumbnails: [457, 730, 580, 126], specs: [457, 872, 580, 122],
    payment: [44, 1002, 992, 74], stitch: [44, 1092, 699, 92], cta: [753, 1092, 283, 92],
    slogan: [60, 1192, 960, 46], social: [44, 1240, 388, 56], website: [448, 1240, 588, 56], powered: [656, 1302, 380, 38]
  });
  const paymentIds = ['visa', 'mastercard', 'applePay', 'googlePay', 'eft'];
  const imageCache = new Map();
  const instances = new WeakMap();

  const clean = value => String(value == null ? '' : value).replace(/\s+/g, ' ').trim();
  const safeUrl = url => /^https?:\/\//i.test(clean(url)) || /^\/\//.test(clean(url)) || /^\/[^/]/.test(clean(url)) || /^data:image\/(png|jpeg|webp|svg\+xml);base64,/i.test(clean(url));
  const clamp = (v, a, b) => Math.min(b, Math.max(a, v));

  function roundRect(ctx, x, y, w, h, radius) {
    const r = Math.min(radius, w / 2, h / 2);
    ctx.beginPath(); ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r); ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r); ctx.arcTo(x, y, x + w, y, r); ctx.closePath();
  }

  function measureLines(ctx, str, maxWidth) {
    const words = clean(str).split(' ').filter(Boolean);
    if (!words.length) return [];
    const lines = []; let current = '';
    for (const word of words) {
      if (ctx.measureText(word).width > maxWidth) {
        if (current) { lines.push(current); current = ''; }
        let fragment = '';
        for (const char of Array.from(word)) {
          if (fragment && ctx.measureText(fragment + char).width > maxWidth) {
            lines.push(fragment); fragment = char;
          } else fragment += char;
        }
        current = fragment;
        continue;
      }
      const next = current ? `${current} ${word}` : word;
      if (ctx.measureText(next).width <= maxWidth) current = next;
      else { lines.push(current); current = word; }
    }
    if (current) lines.push(current);
    return lines;
  }

  function textBox(ctx, content, rect, opts = {}) {
    // Guaranteed to fit horizontally and vertically; truncates only after trying all sizes.
    const [x, y, w, h] = rect;
    const text = clean(content);
    if (!text) return { lines: [], truncated: false };
    const { size = 32, min = 16, weight = 700, maxLines = 2, color = C.white, align = 'center',
      leading = 1.15, uppercase = false, letterSpacing = 0 } = opts;
    const actual = uppercase ? text.toUpperCase() : text;
    let last = [];
    let found = null;
    for (let fs = size; fs >= min; fs--) {
      ctx.font = `${weight} ${fs}px ${FONT}`;
      const lines = measureLines(ctx, actual, w - 4);
      last = lines;
      if (lines.length <= maxLines && lines.length * fs * leading <= h) { found = { fs, lines }; break; }
    }
    let truncated = false;
    if (!found) {
      const fs = min;
      ctx.font = `${weight} ${fs}px ${FONT}`;
      const lines = last.slice(0, maxLines);
      truncated = last.length > maxLines;
      if (truncated && lines.length) {
        let s = lines[lines.length - 1];
        while (s && ctx.measureText(s + '…').width > w - 4) s = s.slice(0, -1);
        lines[lines.length - 1] = s.trimEnd() + '…';
      }
      found = { fs, lines };
    }
    const { fs, lines } = found;
    const rowH = fs * leading;
    const totalH = lines.length * rowH;
    ctx.save(); ctx.font = `${weight} ${fs}px ${FONT}`;
    ctx.fillStyle = color; ctx.textBaseline = 'middle'; ctx.textAlign = align;
    if ('letterSpacing' in ctx) ctx.letterSpacing = `${letterSpacing}px`;
    const centerX = align === 'center' ? x + w / 2 : align === 'left' ? x : x + w;
    for (let i = 0; i < lines.length; i++) {
      ctx.fillText(lines[i], centerX, y + (h - totalH) / 2 + rowH * (i + .5), w - 3);
    }
    ctx.restore(); return { lines, truncated, fontSize: fs };
  }

  function panel(ctx, rect, radius = 20, stroke = '#4c7089', alpha = 0.96) {
    const [x, y, w, h] = rect; ctx.save();
    const g = ctx.createLinearGradient(x, y, x + w, y + h);
    g.addColorStop(0, `rgba(7,19,29,${alpha})`); g.addColorStop(1, `rgba(6,13,23,${alpha})`);
    roundRect(ctx, x, y, w, h, radius); ctx.fillStyle = g; ctx.fill();
    ctx.strokeStyle = stroke; ctx.lineWidth = 1.8; ctx.stroke();
    ctx.restore();
  }

  function frame(ctx, rect, fill = '#f7f9fc', radius = 22) {
    const [x, y, w, h] = rect;
    ctx.save(); ctx.shadowBlur = 12; ctx.shadowColor = 'rgba(0,150,255,.45)';
    roundRect(ctx, x, y, w, h, radius); ctx.fillStyle = '#050d14'; ctx.fill();
    ctx.shadowBlur = 0; ctx.strokeStyle = C.cyan; ctx.lineWidth = 3; ctx.stroke();
    roundRect(ctx, x + 7, y + 7, w - 14, h - 14, radius - 4);
    ctx.fillStyle = fill; ctx.fill(); ctx.strokeStyle = '#92acbc'; ctx.lineWidth = 2; ctx.stroke();
    ctx.restore();
  }

  async function loadImage(url, timeout = 10000) {
    const src = clean(url);
    if (!src || !safeUrl(src)) return null;
    if (imageCache.has(src)) return imageCache.get(src);
    const promise = new Promise(resolve => {
      const img = new Image(); let done = false;
      const finish = result => { if (done) return; done = true; clearTimeout(timer); img.onload = null;
        img.onerror = null; resolve(result); };
      const timer = setTimeout(() => finish(null), timeout);
      img.crossOrigin = 'anonymous';
      img.onload = () => finish(img);
      img.onerror = () => finish(null);
      img.src = src;
    });
    const retriable = promise.then(result => { if (!result) imageCache.delete(src); return result; });
    imageCache.set(src, retriable);
    return retriable;
  }

  function contain(ctx, image, rect, pad = 0, borderRadius = 0) {
    if (!image) return false;
    const [x, y, w, h] = rect, iw = image.naturalWidth || image.width, ih = image.naturalHeight || image.height;
    if (!iw || !ih) return false;
    const scale = Math.min((w - 2 * pad) / iw, (h - 2 * pad) / ih);
    const dw = iw * scale, dh = ih * scale;
    ctx.save();
    if (borderRadius) { roundRect(ctx, x, y, w, h, borderRadius); ctx.clip(); }
    ctx.drawImage(image, x + (w - dw) / 2, y + (h - dh) / 2, dw, dh);
    ctx.restore(); return true;
  }

  function cover(ctx, image, rect, alpha = 1) {
    if (!image) return false;
    const [x, y, w, h] = rect, iw = image.naturalWidth || image.width, ih = image.naturalHeight || image.height;
    const scale = Math.max(w / iw, h / ih);
    ctx.save(); ctx.globalAlpha = alpha;
    ctx.drawImage(image, x + (w - iw * scale) / 2, y + (h - ih * scale) / 2, iw * scale, ih * scale);
    ctx.restore(); return true;
  }

  function drawIcon(ctx, key, cx, cy) {
    ctx.save(); ctx.strokeStyle = '#44c9ff'; ctx.fillStyle = '#44c9ff'; ctx.lineWidth = 3;
    ctx.lineCap = 'round'; ctx.lineJoin = 'round';
    if (key === 'wattage' || key === 'voltage' || key === 'power') {
      ctx.beginPath(); ctx.moveTo(cx + 3, cy - 20); ctx.lineTo(cx - 9, cy + 1); ctx.lineTo(cx + 1, cy + 1);
      ctx.lineTo(cx - 6, cy + 20); ctx.lineTo(cx + 12, cy - 6); ctx.lineTo(cx + 2, cy - 6); ctx.closePath(); ctx.stroke();
    } else if (key === 'ipRating' || key === 'protection' || key === 'warranty') {
      ctx.beginPath(); ctx.moveTo(cx, cy - 19); ctx.lineTo(cx + 17, cy - 12);
      ctx.lineTo(cx + 14, cy + 9); ctx.quadraticCurveTo(cx, cy + 25, cx - 14, cy + 9);
      ctx.lineTo(cx - 17, cy - 12); ctx.closePath(); ctx.stroke();
    } else if (key === 'lumens' || key === 'colourTemperature' || key === 'light') {
      ctx.beginPath(); ctx.arc(cx, cy, 9, 0, Math.PI * 2); ctx.stroke();
      for (let a = 0; a < 8; a++) { const ang = a * Math.PI / 4;
        ctx.beginPath(); ctx.moveTo(cx + Math.cos(ang) * 15, cy + Math.sin(ang) * 15);
        ctx.lineTo(cx + Math.cos(ang) * 20, cy + Math.sin(ang) * 20); ctx.stroke(); }
    } else if (key === 'material' || key === 'finish') {
      for (let i = 0; i < 3; i++) { const yy = cy - 10 + i * 10;
        ctx.beginPath(); ctx.moveTo(cx, yy - 7); ctx.lineTo(cx + 16, yy); ctx.lineTo(cx, yy + 7);
        ctx.lineTo(cx - 16, yy); ctx.closePath(); ctx.stroke(); }
    } else if (key === 'dimensions') {
      ctx.strokeRect(cx - 16, cy - 13, 32, 26);
      ctx.beginPath(); ctx.moveTo(cx - 10, cy + 19); ctx.lineTo(cx + 10, cy + 19); ctx.stroke();
    } else if (key === 'color' || key === 'colour') {
      for (let i = 0; i < 3; i++) { ctx.beginPath(); ctx.arc(cx - 11 + i * 11, cy, 7, 0, Math.PI * 2); ctx.stroke(); }
    } else {
      ctx.beginPath(); ctx.arc(cx, cy, 16, 0, Math.PI * 2); ctx.stroke();
      ctx.beginPath(); ctx.arc(cx, cy, 5, 0, Math.PI * 2); ctx.fill();
    }
    ctx.restore();
  }


  function infoIconKey(label) {
    if (/sku/i.test(label)) return 'sku';
    if (/model/i.test(label)) return 'model';
    if (/collection/i.test(label)) return 'collection';
    if (/colour|color|finish/i.test(label)) return 'colour';
    return 'other';
  }

  function drawInfoIcon(ctx, key, cx, cy) {
    ctx.save(); ctx.strokeStyle = '#45d1ff'; ctx.fillStyle = '#45d1ff'; ctx.lineWidth = 2.5;
    ctx.lineCap = 'round'; ctx.lineJoin = 'round';
    if (key === 'sku') {
      for (let i = -2; i <= 2; i++) { ctx.beginPath(); ctx.moveTo(cx + i * 4, cy - 12); ctx.lineTo(cx + i * 4, cy + 12); ctx.stroke(); }
    } else if (key === 'model') {
      ctx.beginPath(); ctx.moveTo(cx - 13, cy - 8); ctx.lineTo(cx - 2, cy - 14); ctx.lineTo(cx + 12, cy - 5); ctx.lineTo(cx + 2, cy + 14); ctx.closePath(); ctx.stroke();
      ctx.beginPath(); ctx.arc(cx, cy, 3, 0, Math.PI * 2); ctx.fill();
    } else if (key === 'collection') {
      ctx.strokeRect(cx - 11, cy - 10, 22, 17); ctx.strokeRect(cx - 7, cy - 5, 22, 17);
    } else if (key === 'colour') {
      ctx.beginPath(); ctx.arc(cx - 7, cy, 5, 0, Math.PI * 2); ctx.stroke();
      ctx.beginPath(); ctx.arc(cx + 2, cy - 3, 5, 0, Math.PI * 2); ctx.stroke();
      ctx.beginPath(); ctx.arc(cx + 10, cy + 2, 5, 0, Math.PI * 2); ctx.stroke();
    } else {
      ctx.beginPath(); ctx.arc(cx, cy, 13, 0, Math.PI * 2); ctx.stroke();
      ctx.beginPath(); ctx.arc(cx, cy, 4, 0, Math.PI * 2); ctx.fill();
    }
    ctx.restore();
  }

  function badgeFromRecord(d) {
    const tags = (d.tags || []).map(v => clean(v).toLowerCase().replace(/[_-]+/g, ' ').trim());
    const has = candidates => tags.some(tag => candidates.includes(tag));
    if (d.isOnSale || has(['sale', 'on sale', 'clearance', 'promotion', 'special offer']))
      return { text: 'SALE', colors: ['#f14e5e', '#e96725'] };
    if (has(['new arrival', 'newly arrived', 'just arrived', 'new in']))
      return { text: 'NEW ARRIVAL', colors: ['#038fc8', '#00896f'] };
    if (has(['featured', 'featured product', 'staff pick']))
      return { text: 'FEATURED', colors: ['#7955c8', '#127cca'] };
    return null;
  }

  function drawHeroBadge(ctx, rect, badge) {
    if (!badge) return;
    const [x, y, w] = rect;
    const bw = Math.min(186, Math.max(112, badge.text.length * 12));
    const bh = 42;
    const bx = x + w - bw - 18;
    const by = y + 18;
    ctx.save();
    const g = ctx.createLinearGradient(bx, by, bx + bw, by);
    g.addColorStop(0, badge.colors[0]); g.addColorStop(1, badge.colors[1]);
    roundRect(ctx, bx, by, bw, bh, 14); ctx.fillStyle = g; ctx.fill();
    ctx.shadowColor = 'rgba(0,0,0,.18)'; ctx.shadowBlur = 8; ctx.strokeStyle = 'rgba(255,255,255,.5)'; ctx.lineWidth = 1.2; ctx.stroke();
    ctx.restore();
    textBox(ctx, badge.text, [bx + 8, by + 5, bw - 16, bh - 10], { size: 18, min: 14, maxLines: 1, weight: 800, color: '#ffffff' });
  }

  function titleOptions() {
    return { size: 54, min: 30, maxLines: 2, weight: 800, letterSpacing: .2 };
  }

  function shortTypeLabel(value) { return compactText(value || '—', 28); }

  function specBoxes(recordSpecs) { return recordSpecs; }

  function specList(record) {
    const src = record.specs || {};
    const candidates = [
      ['wattage', 'WATTAGE'], ['voltage', 'VOLTAGE'], ['lumens', 'LUMENS'],
      ['colourTemperature', 'LIGHT TEMP.'], ['ipRating', 'IP RATING'], ['dimensions', 'DIMENSIONS'],
      ['material', 'MATERIAL'], ['finish', 'FINISH'], ['warranty', 'WARRANTY'], ['dimmable', 'DIMMABLE'],
      ['lightSource', 'LIGHT SOURCE'], ['fittingType', 'FITTING'], ['mounting', 'MOUNTING'],
      ['currentRating', 'CURRENT'], ['beamAngle', 'BEAM ANGLE'], ['cutoutSize', 'CUTOUT']
    ].filter(([key]) => clean(src[key]));
    const t = clean(record.productType + ' ' + record.title).toLowerCase();
    let order = ['wattage','colourTemperature','lumens','ipRating','voltage','dimensions','material','warranty','finish','dimmable','lightSource','fittingType','mounting','currentRating','beamAngle','cutout'];
    if (/cable|wire|conduit|clip|connector|terminal|gland/.test(t)) order = ['dimensions','material','currentRating','finish','voltage','ipRating','warranty','wattage','lumens'];
    else if (/breaker|switch|socket|surge|protection|rcd|plug|relay/.test(t)) order = ['currentRating','voltage','ipRating','wattage','dimensions','material','warranty','finish'];
    else if (/solar|battery|inverter|charger/.test(t)) order = ['voltage','wattage','currentRating','ipRating','dimensions','warranty','material','finish'];
    const rank = key => { const index = order.indexOf(key); return index < 0 ? 100 : index; };
    return candidates.sort((a,b) => rank(a[0]) - rank(b[0])).map(([key,label]) => ({ key,label,value: clean(src[key]) }));
  }

  function compactText(s, max = 58) { const v = clean(s); return v.length > max ? v.slice(0, max-1).trim() + '…' : v; }

  function selectFeatures(rec, specs) {
    // Explicit title phrases are allowed as copy, not inferred technical performance claims.
    // E.g. 'Double Head Spray Fan' -> Double Head and Spray Function.
    const t = clean(rec.title).toLowerCase();
    const out = [];
    const phrases = [
      [/\bdouble[- ]head\b/, 'DOUBLE HEAD', 'FEATURE', 'other'],
      [/\bspray\b/, 'SPRAY', 'FUNCTION', 'other'],
      [/\brechargeable\b/, 'RECHARGEABLE', 'FEATURE', 'power'],
      [/\bmotion sensor\b/, 'MOTION SENSOR', 'FEATURE', 'other'],
      [/\bdimmable\b/, 'DIMMABLE', 'FEATURE', 'light'],
      [/\bremote control\b/, 'REMOTE', 'CONTROL', 'other']
    ];
    for (const [pattern,value,label,key] of phrases) if (pattern.test(t)) out.push({value,label,key});
    const skip = new Set(specs.map(s => s.key));
    for (const s of specList(rec)) if (!skip.has(s.key)) out.push({ ...s });
    return out.slice(0,3);
  }

  function thumbnailIndexes(data, heroIndex) {
    return (data.images || []).map((_, i) => i).filter(i => i !== heroIndex).slice(0, 3);
  }

  function formatRecord(data, overrides = {}) {
    const d = { ...data, ...overrides };
    const variants = Array.isArray(data.variants) ? data.variants : [];
    const variant = variants.find(v => String(v.id) === String(overrides.variantId || data.variantId)) || variants[0] || {};
    return {
      ...d, variant, title: clean(overrides.title == null ? data.title : overrides.title),
      description: clean(overrides.description == null ? data.shortDescription : overrides.description),
      price: clean(variant.price || data.price), sku: clean(variant.sku || data.sku),
      model: clean(data.model), brand: clean(data.brand), type: clean(data.productType),
      colour: clean(variant.colour || data.colour), finish: clean(data.finish),
      collection: clean(data.collection), brandLogo: clean(data.brandLogo),
      compareAtPrice: clean(data.compareAtPrice), isOnSale: typeof variant.isOnSale === 'boolean' ? variant.isOnSale : !!data.isOnSale,
      tags: Array.isArray(data.tags) ? data.tags : [],
      images: Array.isArray(data.images) ? data.images : [],
      heroIndex: clamp(Number(overrides.heroIndex || 0), 0, Math.max(0, (data.images || []).length - 1)),
      showStitch: !!overrides.showStitch, showPayments: overrides.showPayments !== false,
      specs: data.specs || {}
    };
  }

  async function renderCard(data, overrides = {}) {
    if (document.fonts && document.fonts.load) {
      try { await Promise.race([document.fonts.load('800 40px Poppins'), new Promise(resolve => setTimeout(resolve, 1200))]); }
      catch (_) { /* Canvas falls back to the local sans-serif stack. */ }
    }
    const d = formatRecord(data, overrides); const a = data.assets || {};
    const ctxCanvas = document.createElement('canvas'); ctxCanvas.width = W; ctxCanvas.height = H;
    const ctx = ctxCanvas.getContext('2d', { alpha: false });
    if (!ctx) throw new Error('Canvas 2D rendering is unavailable.');
    const specCandidates = specList(d); const specs = specCandidates.slice(0, 3);
    const features = selectFeatures(d, specs);
    const thumbIds = thumbnailIndexes(d, d.heroIndex);
    const requested = {
      background: a.background, logo: a.logo, house: a.house,
      cta: a.cta, stitch: a.stitch, social: a.social, brandLogo: d.brandLogo,
      pinterest: a.pinterest, facebook: a.facebook, instagram: a.instagram, youtube: a.youtube,
      hero: d.images[d.heroIndex]?.src,
      ...Object.fromEntries(thumbIds.map((id, i) => ['thumb'+i, d.images[id]?.src])),
      ...Object.fromEntries(paymentIds.map(key => [key, a[key]]))
    };
    const assets = Object.fromEntries(await Promise.all(Object.entries(requested).map(async ([k,v]) => [k, await loadImage(v)])));
    const warnings = [];
    const hero = assets.hero;
    if (!hero) warnings.push('A product image could not be loaded. PNG export is disabled until the image is available.');

    // Background uses the user's supplied, less-blue smoky artwork, restrained for readability.
    ctx.fillStyle = C.bg; ctx.fillRect(0,0,W,H);
    if (assets.background) cover(ctx, assets.background, [0,0,W,H], .97);
    const overlay = ctx.createLinearGradient(0,0,0,H);
    overlay.addColorStop(0,'rgba(2,9,15,.05)'); overlay.addColorStop(.25,'rgba(2,8,13,.24)');
    overlay.addColorStop(1,'rgba(0,3,8,.55)');
    ctx.fillStyle = overlay; ctx.fillRect(0,0,W,H);

    // Header / brand
    if (assets.logo) contain(ctx, assets.logo, BOX.logo);
    else textBox(ctx, 'WHITEHOUSE LIGHTING & ELECTRICAL', BOX.logo, { size: 38, min: 30, maxLines: 2, weight: 800 });
    if (assets.house) {
      const [hx, hy, hw, hh] = BOX.house;
      ctx.save();
      const glow = ctx.createRadialGradient(hx + hw / 2, hy + hh / 2, 10, hx + hw / 2, hy + hh / 2, hw * 0.7);
      glow.addColorStop(0, 'rgba(92, 228, 255, .30)');
      glow.addColorStop(.55, 'rgba(17, 121, 255, .12)');
      glow.addColorStop(1, 'rgba(17, 121, 255, 0)');
      ctx.fillStyle = glow;
      ctx.beginPath();
      ctx.arc(hx + hw / 2, hy + hh / 2, hw * 0.62, 0, Math.PI * 2);
      ctx.fill();
      ctx.shadowColor = 'rgba(35,197,255,.55)';
      ctx.shadowBlur = 24;
      contain(ctx, assets.house, BOX.house, 0);
      ctx.restore();
    }
    ctx.save();
    ctx.shadowColor = 'rgba(68, 198, 255, .18)';
    ctx.shadowBlur = 10;
    const titleFitted=textBox(ctx, d.title, BOX.title, titleOptions());
    ctx.restore();
    if (titleFitted.truncated) warnings.push('Title truncated to fit the fixed heading area.');
    if (d.description) {
      const descFitted=textBox(ctx, d.description, BOX.summary, { size: 22, min: 17, maxLines: 2, weight: 600, color: '#d8eaf4' });
      if (descFitted.truncated) warnings.push('Short description truncated to fit.');
    }
    else textBox(ctx, d.type, BOX.summary, { size: 21, min: 17, maxLines: 1, color: '#d8eaf4' });

    // Main data panel
    panel(ctx, BOX.left, 25, '#668298');
    const [lx,ly,lw] = BOX.left;
    const topY = ly + 16, side = 16, columnGap = 12, cardW = (lw - side*2 - columnGap)/2;
    panel(ctx,[lx+side,topY,cardW,100],16,'#168dd7');
    panel(ctx,[lx+side+cardW+columnGap,topY,cardW,100],16,'#168dd7');
    textBox(ctx,'BRAND',[lx+side+8,topY+8,cardW-16,18],{size:14,min:13,maxLines:1,weight:800,color:C.cyan});
    if (assets.brandLogo) contain(ctx, assets.brandLogo, [lx+side+14, topY+26, cardW-28, 58], 2);
    else textBox(ctx,d.brand || '—',[lx+side+11,topY+33,cardW-22,54],{size:20,min:13,maxLines:3,weight:750});
    textBox(ctx,'PRODUCT TYPE',[lx+side+cardW+columnGap+8,topY+8,cardW-16,18],{size:14,min:12,maxLines:1,weight:800,color:C.cyan});
    drawIcon(ctx, 'light', lx+side+cardW+columnGap+32, topY+57);
    textBox(ctx, shortTypeLabel(d.type), [lx+side+cardW+columnGap+54,topY+29,cardW-64,50], {size:18,min:12,maxLines:3,weight:750,align:'left'});

    const info = [
      ['SKU', d.sku], ['MODEL / MPN', d.model], ['COLLECTION', d.collection],
      ['COLOUR / FINISH', clean(d.colour || d.finish)]
    ].filter(([,val]) => clean(val));
    const rowY = ly + 135, infoArea = 247, n = info.length, rowH = n ? Math.min(60, Math.floor(infoArea/n)) : 0;
    info.forEach(([label,val], i) => {
      const y = rowY + i * rowH;
      if (i) { ctx.save();ctx.strokeStyle='rgba(156,185,203,.3)';ctx.beginPath();ctx.moveTo(lx+23,y);ctx.lineTo(lx+lw-23,y);ctx.stroke();ctx.restore();}
      drawInfoIcon(ctx, infoIconKey(label), lx+43, y + rowH/2);
      textBox(ctx,label,[lx+74,y+4,lw-96,20],{size:14,min:13,maxLines:1,weight:800,color:C.muted,align:'left'});
      textBox(ctx,val,[lx+74,y+24,lw-99,rowH-27],{size:22,min:15,maxLines:2,weight:700,align:'left'});
    });
    if (!info.length) textBox(ctx,'Product information',[lx+26,rowY,lw-52,120],{size:22,min:18});

    // Feature chips: maximum 3, exactly same height and width, no fake details.
    const fy = ly + 409, fw = (lw - 38 - 18)/3;
    if (features.length) features.forEach((f,i) => {
      const x = lx + 19 + i*(fw+9);
      panel(ctx,[x,fy,fw,102],18,'#17aef0');
      drawIcon(ctx, f.key, x+fw/2, fy+28);
      textBox(ctx,f.value,[x+7,fy+50,fw-14,25],{size:19,min:12,maxLines:1,weight:800});
      textBox(ctx,f.label,[x+7,fy+74,fw-14,18],{size:12,min:10,maxLines:1,weight:750,color:C.muted});
    });
    else textBox(ctx,'Details verified on product page',[lx+23,fy,lw-46,94],{size:16,min:15,maxLines:2,color:C.muted});

    // Price: reserved, never superimposed on feature chips.
    const py=ly+525; const priceBox=[lx+17,py,lw-34,111];
    panel(ctx,priceBox,22,'#20c8ff');
    ctx.save();const shine=ctx.createLinearGradient(lx,py,lx+lw,py+111);
    shine.addColorStop(0,'rgba(0,110,220,.24)');shine.addColorStop(1,'rgba(0,40,100,.12)');
    roundRect(ctx,...priceBox,22);ctx.fillStyle=shine;ctx.fill();ctx.restore();
    ctx.save();
    const priceBanner=[lx+22, py+10, 128, 30];
    const pg=ctx.createLinearGradient(priceBanner[0], priceBanner[1], priceBanner[0]+priceBanner[2], priceBanner[1]);
    pg.addColorStop(0,'rgba(0,174,255,.95)'); pg.addColorStop(1,'rgba(0,100,245,.9)');
    roundRect(ctx, ...priceBanner, 12); ctx.fillStyle=pg; ctx.fill();
    ctx.restore();
    textBox(ctx,'PRICE',[lx+31,py+10,110,24],{size:18,min:17,weight:800,color:'#ffffff',align:'left',maxLines:1});
    textBox(ctx,d.price||'See online price',[lx+29,py+36,lw-58,66],{size:53,min:26,maxLines:1,weight:800});

    // Product image panel: image cannot overlap or spill outside frame.
    const hi = thumbIds.length ? BOX.hero : [BOX.hero[0],BOX.hero[1],BOX.hero[2],515];
    frame(ctx,hi);
    if (hero) contain(ctx,hero,[hi[0]+20,hi[1]+20,hi[2]-40,hi[3]-40],9);
    else textBox(ctx,'PRODUCT IMAGE UNAVAILABLE',[hi[0]+30,hi[1]+60,hi[2]-60,hi[3]-120],{size:25,min:20,maxLines:2,weight:800,color:'#748595'});
    drawHeroBadge(ctx, hi, badgeFromRecord(d));
    if (thumbIds.length) {
      const [tx,ty,tw,th]=BOX.thumbnails, gap=15;
      const nThumb=thumbIds.length;
      const thumbW=(tw-gap*(nThumb-1))/nThumb;
      thumbIds.forEach((_,i)=>{
        const x=tx+i*(thumbW+gap);
        frame(ctx,[x,ty,thumbW,th],'#f8fafc',17);
        const img=assets['thumb'+i];
        if(img) contain(ctx,img,[x+10,ty+10,thumbW-20,th-20],4);
        else textBox(ctx,'NO IMAGE',[x+12,ty+42,thumbW-24,35],{size:14,min:12,maxLines:1,color:C.muted});
      });
    }

    // Verified specs only; horizontally centered when fewer than 3.
    const [sx,sy,sw,sh]=BOX.specs;
    if (specs.length) {
      const nSpec=specs.length, gap=14, specW=(sw-(nSpec-1)*gap)/nSpec;
      specs.forEach((spec,i)=>{
        const x=sx+i*(specW+gap);
        panel(ctx,[x,sy,specW,sh],17,'#6b95ae');
        drawIcon(ctx,spec.key,x+30,sy+sh/2);
        textBox(ctx,spec.label,[x+56,sy+18,specW-66,24],{size:15,min:12,maxLines:1,weight:800,color:C.muted,align:'left'});
        textBox(ctx,spec.value,[x+56,sy+48,specW-66,50],{size:22,min:14,maxLines:2,weight:800,align:'left'});
      });
    } else panel(ctx,[sx,sy,sw,sh],17,'#63849a');

    // Checkout information with exact branded payment badges.
    panel(ctx,BOX.payment,20,'#3c9dcc');
    const [px,payY,payW] = BOX.payment;
    textBox(ctx,'PAY YOUR WAY',[px+10,payY+13,174,48],{size:19,min:15,weight:800,maxLines:2});
    ctx.save();ctx.strokeStyle='#28bffa';ctx.lineWidth=1.5;ctx.beginPath();ctx.moveTo(px+190,payY+12);ctx.lineTo(px+190,payY+62);ctx.stroke();ctx.restore();
    const chosen=(d.paymentMethods||paymentIds).filter(key=>paymentIds.includes(key));
    if(d.showPayments && chosen.length){
      const gap=8, wx=(payW-218-(chosen.length-1)*gap)/chosen.length;
      chosen.forEach((key,i)=>{
        const bx=px+206+i*(wx+gap),by=payY+6;
        if(assets[key]) contain(ctx,assets[key],[bx,by,wx,62],0);
        else {
          ctx.save();roundRect(ctx,bx+4,by+6,wx-8,50,12);ctx.fillStyle='#ffffff';ctx.fill();ctx.restore();
          textBox(ctx,key,[bx+8,by+15,wx-16,28],{size:14,min:11,maxLines:1,color:'#151f29'});
        }
      });
    } else textBox(ctx,'Payment options at checkout',[px+213,payY+12,payW-234,43],{size:23,min:18,maxLines:1,weight:650,color:C.muted});

    // Stitch is opt-in only: merchant must verify activation and exact promotional terms.
    if (d.showStitch && assets.stitch) {
      const [bx,by,bw,bh] = BOX.stitch;
      panel(ctx, BOX.stitch, 20, '#8756ed');
      const image = assets.stitch;
      const iw = image.naturalWidth || image.width, ih = image.naturalHeight || image.height;
      ctx.save();
      roundRect(ctx,bx+10,by+11,200,bh-22,14);ctx.clip();
      ctx.drawImage(image,iw*.044,ih*.33,iw*.29,ih*.35,bx+10,by+11,200,bh-22);
      ctx.restore();
      ctx.save();ctx.strokeStyle='#38ccff';ctx.lineWidth=1.5;ctx.beginPath();ctx.moveTo(bx+224,by+12);ctx.lineTo(bx+224,by+bh-12);ctx.stroke();ctx.restore();
      textBox(ctx,'BUY NOW AND PAY LATER',[bx+240,by+7,bw-256,35],{size:24,min:18,maxLines:1,weight:800});
      panel(ctx,[bx+254,by+50,167,29],14,'#15b5fd');
      panel(ctx,[bx+435,by+50,244,29],14,'#15b5fd');
      textBox(ctx,'0% interest',[bx+262,by+52,153,25],{size:16,min:13,maxLines:1,weight:800});
      textBox(ctx,'2–6 months to pay',[bx+443,by+52,228,25],{size:16,min:13,maxLines:1,weight:800});
    } else {
      panel(ctx,BOX.stitch,22,'#4e8ba9');
      textBox(ctx,'DISCOVER MORE LIGHTING & ELECTRICAL',[BOX.stitch[0]+24,BOX.stitch[1]+15,BOX.stitch[2]-48,58],{size:23,min:18,maxLines:2,weight:800});
    }
    if (assets.cta) contain(ctx, assets.cta, BOX.cta);
    else { panel(ctx,BOX.cta,24,'#18c4ff');textBox(ctx,'SHOP NOW',[BOX.cta[0]+15,BOX.cta[1]+20,BOX.cta[2]-30,46],{size:27,min:22,weight:800}); }

    // Footer: larger slogan above the social and website row.
    ctx.save();
    ctx.shadowColor = 'rgba(120,230,255,.28)';
    ctx.shadowBlur = 12;
    textBox(ctx,'From Sunlight to Switch — We Power It All',BOX.slogan,{size:26,min:18,maxLines:1,weight:800,color:'#f3fbff'});
    ctx.restore();

    // Social panel.
    panel(ctx, BOX.social, 18, '#2d89b5');
    const [socialX,socialY,socialW,socialH]=BOX.social;
    const socialIcons=['pinterest','facebook','instagram','youtube'];
    socialIcons.forEach((key,i)=>{const image=assets[key];
      if(image) contain(ctx,image,[socialX+10+i*40,socialY+10,34,34]);
    });
    textBox(ctx,'whitehouse_lighting',[socialX+176,socialY+10,socialW-184,36],{size:20,min:14,maxLines:1,weight:800,align:'left'});

    // Website panel with stronger glow so the URL stands out.
    panel(ctx, BOX.website, 20, '#35cbff');
    const [wx, wy, ww, wh] = BOX.website;
    ctx.save();
    ctx.shadowColor = 'rgba(36, 205, 255, .28)';
    ctx.shadowBlur = 16;
    ctx.strokeStyle = 'rgba(95,231,255,.78)';
    ctx.lineWidth = 1.4;
    roundRect(ctx, wx + 7, wy + 7, ww - 14, wh - 14, 16);
    ctx.stroke();
    ctx.restore();
    textBox(ctx,'www.whitehouselightingandelectrical.co.za',BOX.website,{size:24,min:16,maxLines:1,weight:800,color:'#ffffff'});
    ctx.save();
    ctx.strokeStyle = 'rgba(78,225,255,.65)';
    ctx.lineWidth = 1.2;
    ctx.beginPath();
    ctx.moveTo(wx + 18, wy + 12);
    ctx.lineTo(wx + 58, wy + 12);
    ctx.moveTo(wx + ww - 58, wy + wh - 12);
    ctx.lineTo(wx + ww - 18, wy + wh - 12);
    ctx.stroke();
    ctx.restore();

    // Two-line powered-by credit in a reserved footer area below website/social row.
    ctx.save();
    ctx.textBaseline = 'top';
    ctx.textAlign = 'left';
    const baseX = BOX.powered[0] + BOX.powered[2];
    const y1 = BOX.powered[1];
    const y2 = BOX.powered[1] + 18;
    const seg1a = 'Powered by ';
    const seg1b = 'H@z0 T3c#';
    const seg1c = ' -';
    ctx.font = '700 12px Arial, Helvetica, sans-serif';
    const segWidths = [seg1a, seg1b, seg1c].map(v => ctx.measureText(v).width);
    let cursor = baseX - segWidths.reduce((a,b) => a+b, 0);
    ctx.fillStyle = '#eef8ff'; ctx.fillText(seg1a, cursor, y1); cursor += segWidths[0];
    ctx.fillStyle = '#24b8ff'; ctx.fillText(seg1b, cursor, y1); cursor += segWidths[1];
    ctx.fillStyle = '#eef8ff'; ctx.fillText(seg1c, cursor, y1);
    ctx.font = '700 11px Arial, Helvetica, sans-serif';
    ctx.textAlign = 'right'; ctx.fillText('If you Can Dream It We Can Build It', baseX, y2);
    ctx.restore();

    return { canvas:ctxCanvas, warnings, hasHero:!!hero, specCount:specs.length, featureCount:features.length, thumbCount:thumbIds.length };
  }

  const downloadBlob = (canvas, filename) => new Promise((resolve,reject)=>{
    try {
      canvas.toBlob(blob=>{
        if (!blob) { reject(new Error('PNG export could not be created (possibly a cross-origin image).')); return; }
        const url=URL.createObjectURL(blob),link=document.createElement('a');
        link.href=url;link.download=filename;document.body.appendChild(link);link.click();link.remove();
        setTimeout(()=>URL.revokeObjectURL(url),1000);resolve();
      },'image/png');
    } catch(e){ reject(e); }
  });
  const filenameFor = (data,variant) => {
    const slug=clean(data.handle || data.title).toLowerCase().replace(/[^a-z0-9-]+/g,'-').replace(/-+/g,'-').replace(/^-|-$/g,'');
    const variantSlug=clean(variant?.sku||'').toLowerCase().replace(/[^a-z0-9-]+/g,'-').replace(/-+/g,'-');
    return `whitehouse-${slug.slice(0,80)}${variantSlug?'-'+variantSlug.slice(0,40):''}-1080x1350.png`;
  };

  function setTriggerState(trigger, busy, label) {
    if (!trigger) return;
    trigger.disabled = !!busy;
    trigger.setAttribute('aria-busy', busy ? 'true' : 'false');
    const span = trigger.querySelector('span');
    if (span && label) span.textContent = label;
  }

  async function quickDownload(tool, trigger) {
    setTriggerState(trigger, true, 'Preparing export…');
    try {
      const json = tool.querySelector('[data-whle-export-json]');
      if (!json) throw new Error('Product data block is missing.');
      const data = JSON.parse(json.textContent);
      const chosenId = new URL(window.location.href).searchParams.get('variant') || data.variantId;
      const result = await renderCard(data, { variantId: chosenId });
      if (!result.hasHero) throw new Error('A product image is required before exporting the card.');
      const variant = (data.variants || []).find(v => String(v.id) === String(chosenId)) || (data.variants || [])[0];
      await downloadBlob(result.canvas, filenameFor(data, variant));
      setTriggerState(trigger, false, 'Download product card');
    } catch (error) {
      console.error(error);
      setTriggerState(trigger, false, 'Download product card');
      alert('Whitehouse product-card export failed: ' + error.message + '.\n\nTip: Shift-click the button to open the advanced preview editor.');
    }
  }

  function createDialog(tool,data) {
    const dialog=document.createElement('dialog');dialog.className='whle-export-modal';
    dialog.setAttribute('aria-label','Whitehouse product-card exporter');
    dialog.innerHTML=`
      <div class="whle-export-window">
        <header class="whle-export-header">
          <div><strong>Whitehouse Product Card</strong><span>Instagram export • 1080 × 1350 PNG</span></div>
          <button type="button" class="whle-export-close" aria-label="Close exporter">×</button>
        </header>
        <div class="whle-export-grid">
          <div class="whle-export-options">
            <label>Product variant<select data-field="variantId"></select></label>
            <label>Main product image<select data-field="heroIndex"></select></label>
            <label>Display title<textarea rows="2" data-field="title" maxlength="180"></textarea></label>
            <label>Short description<textarea rows="3" data-field="description" maxlength="220"></textarea></label>
            <label class="whle-export-check"><input type="checkbox" data-field="showPayments" checked> Show configured payment methods</label>
            <label class="whle-export-check"><input type="checkbox" data-field="showStitch"> Show Stitch promotional banner (only if enabled and terms are current)</label>
            <p class="whle-export-note">Product details and price come from Shopify. Edits here affect only this downloaded image.</p>
            <button type="button" class="whle-export-refresh">Update preview</button>
            <button type="button" class="whle-export-download" disabled>Download PNG</button>
            <p class="whle-export-status" role="status" aria-live="polite">Preparing preview…</p>
          </div>
          <div class="whle-export-preview"><canvas width="1080" height="1350" aria-label="Product-card preview" role="img"></canvas></div>
        </div>
      </div>`;
    document.body.appendChild(dialog);
    const field=name=>dialog.querySelector(`[data-field="${name}"]`);
    const variantSel=field('variantId'),heroSel=field('heroIndex'),title=field('title'),description=field('description');
    const status=dialog.querySelector('.whle-export-status');
    const download=dialog.querySelector('.whle-export-download');
    const preview=dialog.querySelector('canvas');
    const variants=Array.isArray(data.variants)?data.variants:[];
    if(variants.length){
      variants.forEach(v=>{const o=document.createElement('option');o.value=String(v.id);o.textContent=`${clean(v.title||'Default')} — ${clean(v.price||'')}${v.sku?' ['+v.sku+']':''}`;variantSel.appendChild(o);});
      const fromUrl=new URL(location.href).searchParams.get('variant');
      variantSel.value=String(data.variantId&&variants.some(v=>String(v.id)===String(data.variantId))?data.variantId:(fromUrl&&variants.some(v=>String(v.id)===fromUrl)?fromUrl:variants[0].id));
    } else {const o=document.createElement('option');o.textContent='Default';o.value='';variantSel.appendChild(o);}
    (data.images||[]).forEach((img,i)=>{const o=document.createElement('option');o.value=String(i);o.textContent=`Image ${i+1}${img.alt?' — '+clean(img.alt).slice(0,45):''}`;heroSel.appendChild(o);});
    if(!heroSel.options.length){const o=document.createElement('option');o.textContent='No product images';o.value='0';heroSel.appendChild(o);}
    title.value=data.title||'';description.value=data.shortDescription||'';
    dialog.querySelector('.whle-export-close').addEventListener('click',()=>dialog.close());
    dialog.addEventListener('click',e=>{if(e.target===dialog) dialog.close();});
    dialog.addEventListener('close',()=>{dialog.remove();instances.delete(tool);});
    let current=null;let generation=0;let busy=false;
    async function update(){
      const mine=++generation;busy=true;download.disabled=true;status.textContent='Rendering 1080 × 1350 preview…';
      const overrides={variantId:variantSel.value,heroIndex:parseInt(heroSel.value||'0',10),title:title.value,
        description:description.value,showStitch:field('showStitch').checked,showPayments:field('showPayments').checked};
      try {
        const result=await renderCard(data,overrides);
        if(mine!==generation)return;
        current={...result, overrides};
        const c=preview.getContext('2d');c.clearRect(0,0,W,H);c.drawImage(result.canvas,0,0);
        if(result.hasHero){download.disabled=false;status.textContent=`Ready • ${result.specCount} specifications • ${result.featureCount} feature chips • ${result.thumbCount} supporting images${result.warnings.length ? ' • '+result.warnings.join(' ') : ''}`;}
        else status.textContent=result.warnings.join(' ')||'Image is missing. Export disabled.';
      }catch(err){if(mine===generation)status.textContent=`Preview error: ${err.message}`;}
      finally{if(mine===generation)busy=false;}
    }
    dialog.querySelector('.whle-export-refresh').addEventListener('click',update);
    let inputTimer;
    for (const editable of [title,description]) editable.addEventListener('input',()=>{
      download.disabled=true;status.textContent='Updating edited text…';clearTimeout(inputTimer);
      inputTimer=setTimeout(update,450);
    });
    variantSel.addEventListener('change',update);heroSel.addEventListener('change',update);
    field('showStitch').addEventListener('change',update);field('showPayments').addEventListener('change',update);
    download.addEventListener('click',async()=>{
      if(busy||!current||!current.hasHero)return;
      download.disabled=true;status.textContent='Preparing PNG download…';
      try {const variant=(data.variants||[]).find(v=>String(v.id)===String(current.overrides.variantId));
        await downloadBlob(current.canvas,filenameFor(data,variant));status.textContent='PNG download initiated.';
      }catch(err){status.textContent=`Download failed: ${err.message}`;}
      finally{download.disabled=false;}
    });
    return {dialog,update};
  }

  function init(root=document) {
    root.querySelectorAll('[data-whle-exporter]').forEach(tool=>{
      if(tool.dataset.whleBound==='true')return;
      tool.dataset.whleBound='true';
      const trigger=tool.querySelector('[data-whle-export-open]');
      const json=tool.querySelector('[data-whle-export-json]');
      if(!trigger||!json)return;
      trigger.addEventListener('click',(event)=>{
        try {
          if (event.shiftKey) {
            const existing=instances.get(tool);
            if(existing){existing.dialog.showModal();return;}
            const data=JSON.parse(json.textContent);
            const ui=createDialog(tool,data);instances.set(tool,ui);
            ui.dialog.showModal();ui.update();
            return;
          }
          quickDownload(tool, trigger);
        } catch(error) {
          alert('Whitehouse product-card exporter could not start: '+error.message);
        }
      });
    });
  }

  window.WHLECardExporter={ version:'3.5.0',renderCard,init,boxes:BOX,textBox,formatRecord,measureLines,filenameFor };
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',()=>init());else init();
  document.addEventListener('shopify:section:load',e=>init(e.target));
})();
